import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import API from 'src/services/API';
import { ALL_MANAGE_PERMISSION } from 'src/constants/permission-codes';

import { useAuthContext } from './use-auth-context';

// ----------------------------------------------------------------------

const QUERY_KEY = ['acl', 'my-permissions'] as const;

// ----------------------------------------------------------------------

/**
 * 获取当前用户 ACL 权限集合并提供校验方法
 */
export function useMyPermissions() {
  const { authenticated, user } = useAuthContext();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await API.Acl.AclControllerGetMyPermissionsV1({
        fetchOptions: { showError: false, showSuccess: false },
      });
      return res.data?.data ?? [];
    },
    enabled: authenticated,
    staleTime: 5 * 60 * 1000,
  });

  const permissionSet = useMemo(() => new Set(query.data ?? []), [query.data]);

  /** 检查是否拥有指定权限 */
  const hasPermission = useCallback(
    (permissionCode: string): boolean => {
      if (user?.isAdmin || user?.isSuperAdmin) return true;
      if (permissionSet.has(ALL_MANAGE_PERMISSION)) return true;
      return permissionSet.has(permissionCode);
    },
    [permissionSet, user?.isAdmin, user?.isSuperAdmin]
  );

  /** 检查是否拥有任一权限 */
  const hasAnyPermission = useCallback(
    (permissionCodes: string[]) => permissionCodes.some(hasPermission),
    [hasPermission]
  );

  /** 检查是否拥有全部权限 */
  const hasAllPermissions = useCallback(
    (permissionCodes: string[]) => permissionCodes.every(hasPermission),
    [hasPermission]
  );

  return {
    permissions: query.data ?? [],
    isLoading: query.isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refetch: query.refetch,
  };
}
